import React from 'react';
import { Menu, Info, Dices, Shield, Siren, Crosshair, DollarSign, SkipForward, User, Play, Pause, Eye } from 'lucide-react';
import { TEAMS, TEAM_COLOR, HOME, BASE } from './rtbBoard';

function TeamPanel({ team, seat, positions, active, mine }) {
  const col = TEAM_COLOR[team];
  if (!positions) {
    return (
      <div data-testid={`rtb-team-panel-${team}`} data-active="false" className="flex flex-col justify-center rounded-lg px-1.5 py-1 opacity-50 border border-dashed border-white/15 min-w-0 whitespace-nowrap overflow-hidden">
        <span className="rtb-display text-xs sm:text-lg leading-none" style={{ color: col }}>{team.toUpperCase()}</span>
        <span className="text-[8px] sm:text-[9px] text-white/60">sitting out</span>
      </div>
    );
  }
  const home = positions.filter((p) => p === HOME).length;
  return (
    <div data-testid={`rtb-team-panel-${team}`} data-active={active ? 'true' : 'false'}
      className={`relative flex items-center gap-2 rounded-lg px-1.5 py-1 min-w-0 transition-[transform,box-shadow] duration-300 ${active ? 'scale-[1.04]' : 'opacity-80'}`}
      style={{ background: `linear-gradient(135deg, ${col}40, #0b0b0fdd 70%)`, border: `1.5px solid ${active ? col : `${col}66`}`, boxShadow: active ? `0 0 18px ${col}` : 'none' }}>
      <img src={`/rtb/face_${team}.png`} alt={`${team} crew`} className="w-7 h-7 sm:w-11 sm:h-11 rounded-md object-cover object-top shrink-0" style={{ background: `${col}55`, border: `1px solid ${col}` }} />
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <span className="rtb-display text-[13px] sm:text-lg leading-none" style={{ color: col }}>{team.toUpperCase()}</span>
          {mine && <User size={12} className="text-white" aria-label="you" />}
        </div>
        <div className="hidden sm:block text-[10px] text-white/60 truncate max-w-[110px]" data-testid={`rtb-team-seat-${team}`}>{seat?.kind === 'open' ? 'Open seat' : seat?.name}</div>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="rtb-display text-[11px] sm:text-sm text-white leading-none" data-testid={`rtb-team-home-${team}`}>{home}/4</span>
          {positions.map((p, i) => (
            <span key={i} className="hidden sm:inline-block w-2.5 h-2.5 rounded-full" title={p === HOME ? 'home' : p === BASE ? 'in base' : 'on the block'}
              style={{ background: p === HOME ? '#FDE68A' : p === BASE ? 'transparent' : col, border: `1.5px solid ${p === HOME ? '#FDE68A' : col}` }} />
          ))}
        </div>
      </div>
      {active && <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[8px] rtb-display tracking-widest px-1.5 rounded-sm text-black" style={{ background: col }}>TURN</span>}
    </div>
  );
}

export function RtbTopBar({ cityName, perk, onPerk, match, display, turn, myTeam }) {
  return (
    <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 bg-black/80 border-b border-[#C9A84C]/30 backdrop-blur-md">
      <div className="shrink-0 hidden md:block">
        <div className="leading-none whitespace-nowrap"><span className="rtb-display text-2xl lg:text-3xl text-white">RUN THE </span><span className="rtb-marker text-2xl lg:text-3xl text-[#EF4444]">BLOCK</span></div>
        <div className="text-[8px] tracking-[0.3em] text-white/50">KILLING OVER WATER UNIVERSE</div>
        {cityName && <div data-testid="rtb-city-name" className="text-[10px] tracking-[0.25em] text-[#C9A84C] mt-0.5">{cityName.toUpperCase()}</div>}
        {perk && <button type="button" data-testid="rtb-city-perk" onClick={onPerk} className="mt-1 rounded-full border border-[#C9A84C]/60 bg-[#C9A84C]/10 px-2 py-0.5 text-[9px] tracking-[0.15em] text-[#F3DC8A] hover:bg-[#C9A84C]/20 transition-colors">{perk.toUpperCase()}</button>}
      </div>
      <div className="grid grid-cols-4 gap-1.5 sm:gap-2 flex-1 min-w-0">
        {TEAMS.map((t) => <TeamPanel key={t} team={t} seat={match.seats[t]} positions={display[t]} active={turn === t} mine={myTeam === t} />)}
      </div>
      <div data-testid="rtb-round" className="shrink-0 text-center rounded-lg border border-white/20 bg-black/60 px-2 sm:px-3 py-1">
        <div className="text-[8px] sm:text-[9px] tracking-[0.25em] text-white/60">ROUND</div>
        <div className="rtb-display text-lg sm:text-2xl text-white leading-none">{match.round}</div>
      </div>
    </div>
  );
}

const LEGEND = [[Shield, 'Safe Zone', '#60A5FA'], [Siren, 'Checkpoint', '#FACC15'], [Crosshair, 'Ambush', '#F87171'], [DollarSign, 'Territory', '#4ADE80']];

export function RtbBanner({ text, tone }) {
  if (!text) return null;
  return (
    <div data-testid="rtb-banner" className="absolute left-1/2 -translate-x-1/2 top-2 z-[99997] max-w-[92%] rounded-full border bg-black/80 backdrop-blur-md px-4 sm:px-6 py-1.5 sm:py-2 flex items-center gap-2 text-xs sm:text-base text-white whitespace-nowrap overflow-hidden text-ellipsis rtb-toast"
      style={{ borderColor: tone || '#C9A84C', boxShadow: `0 0 18px ${tone || '#C9A84C'}55` }}>
      <Dices size={16} className="shrink-0" color={tone || '#C9A84C'} />
      <span className="rtb-display tracking-wider text-sm sm:text-xl truncate">{text}</span>
    </div>
  );
}

function SpeedAuto({ speed, setSpeed, auto, onAuto, spectate }) {
  return (
    <div className="hidden sm:flex items-center gap-1.5">
      <div className="flex rounded-lg border border-white/15 bg-white/5 p-0.5" role="radiogroup" aria-label="Speed">
        {[1, 2, 4].map((v) => (
          <button key={v} type="button" data-testid={`rtb-speed-${v}x`} aria-pressed={speed === v} onClick={() => setSpeed(v)}
            className={`h-8 sm:h-10 px-2 sm:px-3 rounded-md rtb-display text-sm sm:text-lg transition-colors ${speed === v ? 'bg-[#C9A84C] text-black' : 'text-white/70 hover:text-white'}`}>{v}x</button>
        ))}
      </div>
      {!spectate && (
        <button type="button" data-testid="rtb-auto-btn" aria-pressed={auto} onClick={onAuto}
          className={`h-9 sm:h-11 px-2.5 sm:px-4 rounded-lg border rtb-display text-sm sm:text-lg tracking-wider flex items-center gap-1 transition-colors ${auto ? 'border-[#4ADE80] bg-[#4ADE80]/15 text-[#4ADE80]' : 'border-white/15 text-white/80 hover:border-[#4ADE80]'}`}>
          {auto ? <Pause size={14} /> : <Play size={14} />} AUTO
        </button>
      )}
    </div>
  );
}

export function RtbBottomBar({ wide, status, onMenu, onRules, canRoll, onRoll, canEnd, onEnd, speed, setSpeed, auto, onAuto, spectate }) {
  return (
    <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 bg-black/85 border-t border-[#C9A84C]/30 backdrop-blur-md">
      <div className="flex items-center gap-2 sm:gap-3 sm:flex-1">
      <button type="button" data-testid="rtb-menu-btn" onClick={onMenu} aria-label="Menu" className="rtb-btn-ghost w-10 h-10 sm:w-12 sm:h-12 shrink-0"><Menu size={20} /></button>
      <button type="button" data-testid="rtb-rules-btn" onClick={onRules} aria-label="Game rules" className="rtb-btn-ghost flex items-center gap-2 px-3 h-10 sm:h-12 text-left shrink-0">
        <Info size={18} className="shrink-0" />
        <span className={`${wide ? 'hidden lg:inline' : 'hidden'} text-[10px] leading-tight text-white/80`}>ROLL to move<br />Capture to send back<br />3 in a row = Combat</span>
      </button>
      </div>
      {spectate ? (
        <div data-testid="rtb-spectating" className="flex-1 sm:flex-none sm:min-w-[220px] h-11 sm:h-14 flex items-center justify-center gap-2 rounded-full border-2 border-[#C9A84C] text-[#F3DC8A] rtb-display text-base sm:text-2xl tracking-wider whitespace-nowrap px-3"><Eye size={20} /> SPECTATING</div>
      ) : (
        <button type="button" data-testid="rtb-roll-btn" onClick={onRoll} disabled={!canRoll}
          className={`rtb-roll flex-1 sm:flex-none sm:min-w-[220px] h-11 sm:h-14 flex items-center justify-center gap-2 rounded-full rtb-display text-base sm:text-2xl tracking-wider whitespace-nowrap px-3 overflow-hidden ${canRoll ? 'rtb-roll-ready' : ''}`}>
          <Dices size={22} className="shrink-0" /> <span className="truncate">{status}</span>
        </button>
      )}
      <div className="flex items-center justify-end gap-2 sm:gap-3 sm:flex-1">
      <SpeedAuto speed={speed} setSpeed={setSpeed} auto={auto} onAuto={onAuto} spectate={spectate} />
      <div data-testid="rtb-legend" className={`${wide ? 'hidden xl:grid' : 'hidden'} grid-cols-2 gap-x-4 gap-y-1 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5`}>
        {LEGEND.map(([I, label, c]) => <span key={label} className="flex items-center gap-1.5 text-[11px] text-white/85"><I size={14} color={c} />{label}</span>)}
      </div>
      {!spectate && <button type="button" data-testid="rtb-end-turn-btn" onClick={onEnd} disabled={!canEnd}
        className="rtb-btn-ghost h-11 sm:h-14 px-3 sm:px-5 rtb-display text-sm sm:text-xl tracking-wider flex items-center gap-1.5 whitespace-nowrap shrink-0 disabled:opacity-35">
        <SkipForward size={18} className={wide ? 'sm:hidden' : ''} /><span className={wide ? 'hidden sm:inline' : 'hidden'}>END TURN</span>
      </button>}
      </div>
    </div>
  );
}
